import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [jobFunctions, setJobFunctions] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    organization: '',
    jobFunction: '',
    companyType: '',
    userType: 'attendee',
  });

  useEffect(() => {
    fetchReferenceData();
  }, []);

  const fetchReferenceData = async () => {
    try {
      const [jobsRes, typesRes] = await Promise.all([
        axios.get(`${API}/job-functions`),
        axios.get(`${API}/company-types`),
      ]);
      setJobFunctions(jobsRes.data);
      setCompanyTypes(typesRes.data);
    } catch (error) {
      console.error('Error fetching reference data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!formData.jobFunction || !formData.companyType) {
      toast.error('Please select your job function and company type');
      return;
    }

    setLoading(true);
    
    try {
      await register({
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        organization: formData.organization || null,
        job_function: formData.jobFunction,
        company_type: formData.companyType,
        user_type: formData.userType,
      });
      toast.success('Account created successfully!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/10812974/pexels-photo-10812974.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 hero-overlay" />
      <div className="relative z-10 w-full max-w-lg mx-4">
        <div className="bg-card border border-border/50 p-8 md:p-10">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="text-2xl text-gold">AgeForward Society</h1>
            </Link>
            <p className="text-muted-foreground mt-2">Join our community</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Account Type */}
            <div className="space-y-3">
              <Label className="text-sm uppercase tracking-wider">Account Type *</Label>
              <RadioGroup 
                value={formData.userType} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, userType: value }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="attendee" id="attendee" />
                  <Label htmlFor="attendee" className="cursor-pointer">Attendee</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="business" id="business" />
                  <Label htmlFor="business" className="cursor-pointer">Business/Organizer</Label>
                </div>
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-sm uppercase tracking-wider">
                Full Name *
              </Label>
              <Input
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="John Smith"
                required
                className="rounded-sm border-border/50"
                data-testid="fullname-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm uppercase tracking-wider">
                Email *
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="rounded-sm border-border/50"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization" className="text-sm uppercase tracking-wider">
                Organization
              </Label>
              <Input
                id="organization"
                name="organization"
                value={formData.organization}
                onChange={handleChange}
                placeholder="Your company or organization"
                className="rounded-sm border-border/50"
                data-testid="organization-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Job Function *</Label>
                <Select 
                  value={formData.jobFunction} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, jobFunction: value }))}
                >
                  <SelectTrigger className="rounded-sm border-border/50" data-testid="job-function-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {jobFunctions.map((job) => (
                      <SelectItem key={job.value} value={job.value}>
                        {job.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-sm uppercase tracking-wider">Company Type *</Label>
                <Select 
                  value={formData.companyType} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, companyType: value }))}
                >
                  <SelectTrigger className="rounded-sm border-border/50" data-testid="company-type-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {companyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm uppercase tracking-wider">
                Password *
              </Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="••••••••"
                required
                minLength={6}
                className="rounded-sm border-border/50"
                data-testid="password-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm uppercase tracking-wider">
                Confirm Password *
              </Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="••••••••"
                required
                className="rounded-sm border-border/50"
                data-testid="confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-primary hover:bg-gold/90 rounded-sm uppercase tracking-widest py-6"
              data-testid="register-btn"
            >
              {loading ? <span className="spinner mr-2" /> : null}
              Create Account
            </Button>
          </form>

          <p className="text-center text-muted-foreground mt-8">
            Already have an account?{' '}
            <Link to="/login" className="text-gold hover:underline underline-offset-4" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
